/**
 * Working in a container on Move Pallets (Mark, 2026-10-15).
 *
 * The driver scans the container at its door, with a signal. The tablet reads
 * every pallet that container's partner has in ANY container now, with which
 * container each is in (`GET /api/pallets/in-partner-containers`), and keeps
 * it while the driver works inside, where there is often no signal. One partner
 * per container, so every pallet here is known to be that partner's.
 *
 * A pallet scanned inside is judged against it, with or without a signal:
 *
 * - on the open container's list: moved as usual;
 * - on another of the partner's containers: the farm loaded the wrong pallet.
 *   The barcode in the driver's hand wins: it is moved anyway, from where the
 *   data said, with a short amber note, and the server writes "Wrong Container"
 *   into the pallet's history (the move carries `foundInContainer`);
 * - on neither: asked of the server with a signal, refused without one
 *   (offlineRules.ts).
 */

import { palletKey } from './palletCache'
import { matchScan } from './scanTypes'
import type { PalletInfo } from './lookupMapping'
import { containerHoldLabel } from './containerHold'

/** One row of the read, as the API sends it. */
export type ContainerPalletDto = {
  palletIdentifier?: string | null
  equipmentNumber?: string | null
  productNumber?: string | null
  productDescription?: string | null
  caseQuantity?: number | null
  weight?: number | null
  onHold?: boolean | null
  holdComments?: string | null
  carryOver?: boolean | null
}

export type ContainerPalletsDto = {
  equipmentNumber?: string | null
  partnerId?: number | null
  partnerName?: string | null
  /** Its Unmapped flag, or null (Mark, 2026-10-16): another container was moved into its cell in BOB. */
  unmapped?: { shownIn?: string | null, displacedBy?: string | null } | null
  pallets?: ContainerPalletDto[] | null
}

/** The open container, and its partner's pallets keyed for a scan to find. */
export type ContainerWork = {
  equipmentNumber: string
  partnerId: number
  partnerName: string
  /** Each pallet as its card shows it; `currentLocation` is the container the data has it in. */
  pallets: Map<string, PalletInfo>
  /** How many the data has in the open container itself. */
  inThisContainer: number
  /** When it was read, for the screen to say. */
  readAt: number
  /**
   * BOB's Unmapped mark, in words, or null (Mark, 2026-10-16): another container was moved
   * into the cell BOB shows this one in. The driver can tell a clerk where it really is.
   */
  unmappedNote: string | null
  /**
   * BOB's No Owner, in words, or null (Mark, 2026-10-16: flagged, never stopped): nobody has said
   * whose the container is. The work goes on; a clerk picks the partner in BOB.
   */
  noOwnerNote: string | null
  /**
   * "1 of 20 on hold", or null (Mark, 2026-10-22): one held pallet holds the whole container, as
   * BOB shows it. The driver may still move it; a held pallet still cannot be loaded.
   */
  holdNote: string | null
}

export function mapContainerWork(dto: ContainerPalletsDto, readAt = Date.now()): ContainerWork {
  const equipmentNumber = (dto.equipmentNumber ?? '').trim().toUpperCase()
  const partnerId = typeof dto.partnerId === 'number' ? dto.partnerId : 0
  const pallets = new Map<string, PalletInfo>()
  let inThisContainer = 0
  let heldInThisContainer = 0
  for (const row of dto.pallets ?? []) {
    const palletId = (row.palletIdentifier ?? '').trim()
    if (palletId === '') continue
    const container = (row.equipmentNumber ?? '').trim().toUpperCase()
    if (container === equipmentNumber) inThisContainer += 1
    if (container === equipmentNumber && row.onHold) heldInThisContainer += 1
    pallets.set(palletKey(palletId), {
      palletId,
      productNumber: row.productNumber ?? '',
      productDescription: row.productDescription ?? '',
      commodity: '',
      caseQuantity: row.caseQuantity ?? 0,
      weight: row.weight ?? 0,
      receivedDate: '',
      currentLocation: container || '—',
      onHold: row.onHold ?? false,
      holdComments: row.holdComments ?? '',
      shipped: false,
      carryOver: row.carryOver ?? false,
      partnerId,
    })
  }
  return {
    equipmentNumber,
    partnerId,
    partnerName: (dto.partnerName ?? '').trim(),
    pallets,
    inThisContainer,
    readAt,
    unmappedNote: unmappedNoteOf(dto.unmapped),
    noOwnerNote: typeof dto.partnerId === 'number' ? null : noOwnerNote(equipmentNumber),
    holdNote: containerHoldLabel(heldInThisContainer, inThisContainer),
  }
}

/** The amber line for a container with No Owner: the same words as BOB's notice (utils/noOwner.ts there). */
export function noOwnerNote(equipmentNumber?: string | null): string {
  const which = (equipmentNumber ?? '').trim() || 'this container'
  return `No Owner: nobody has said whose ${which} is. It is not stopped; a clerk picks its partner on Yard > Containers (No Owner).`
}

/** The amber line for a container BOB is not sure of: the same words as BOB's mark. */
export function unmappedNoteOf(flag: ContainerPalletsDto['unmapped']): string | null {
  if (!flag) return null
  const shownIn = (flag.shownIn ?? '').trim()
  const other = (flag.displacedBy ?? '').trim()
  const where = shownIn ? ` BOB shows it in ${shownIn}` : ''
  const why = other ? `, but ${other} was moved there` : ''
  return `Unmapped:${where}${why}. Tell a clerk where it is.`
}

export type ContainerVerdict =
  | { kind: 'here', pallet: PalletInfo }
  | { kind: 'elsewhere', pallet: PalletInfo, dataSaid: string }
  | { kind: 'unknown' }

/** Where the open container's list says a scanned pallet is. */
export function judgeContainerPallet(work: ContainerWork, scanned: string): ContainerVerdict {
  const found = work.pallets.get(palletKey(scanned))
  if (!found) return { kind: 'unknown' }
  if (found.currentLocation === work.equipmentNumber) return { kind: 'here', pallet: { ...found } }
  return {
    kind: 'elsewhere',
    dataSaid: found.currentLocation,
    pallet: { ...found, dataPlace: found.currentLocation },
  }
}

/** The amber note on the card of a pallet found in the wrong container: another container, or the floor. */
export function wrongContainerNote(dataSaid: string): string {
  const isContainer = matchScan(dataSaid, ['container'], { allowBare: false }) !== null
  return isContainer
    ? `In Container ${dataSaid} per data — moved anyway`
    : `At ${dataSaid} per data — moved anyway`
}

/**
 * A pallet the server found, while a container is open (Mark, 2026-10-15): on no container list,
 * so the data has it somewhere else -- the floor, or another partner's container. It was found in
 * this one, so it is moved anyway and the server records Wrong Container.
 */
export function foundElsewhere(work: ContainerWork, pallet: PalletInfo): PalletInfo {
  const place = pallet.currentLocation.trim()
  if (place === '' || place === '—' || place.toUpperCase() === work.equipmentNumber) return pallet
  return { ...pallet, dataPlace: place }
}
